import { FocusTrapRegion } from '@ariakit/react/focus-trap';
import { motion, useReducedMotion } from 'motion/react';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocation } from 'react-router';
import logoSrc from 'src/assets/prisma-logo-short-colored.svg';
import { Footer } from 'src/components/layout/app-shell/footer';
import { ScrollToTopButton } from 'src/components/layout/app-shell/scroll-to-top-button';
import { TopBar } from 'src/components/layout/app-shell/top-bar';
import { MenuIcon } from 'src/components/media-and-icons/icons/menu';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { stickyHeaderId } from 'src/components/scroll-management';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import {
  useHeaderHeight,
  useHeaderHeightMeasure,
} from 'src/hooks/use-header-height';
import { cn } from 'src/utils/cn';

type AppShellManagerProps = {
  contextualSidebarTargetRef: HTMLDivElement | null;
  sidebar: (props: {
    isExpanded: boolean;
    toggleSidebarPreference: ReactNode;
    expandSidebarForInteraction: () => void;
  }) => ReactNode;
  contextualSidebar: ReactNode;
  children: ReactNode;
};

export const AppShellManager: FC<AppShellManagerProps> = ({
  contextualSidebarTargetRef,
  sidebar,
  contextualSidebar,
  children,
}) => {
  const { md, xl } = useBreakpoints();
  const { pathname, hash } = useLocation();

  // User preference state
  const [sidebarPreferredExpanded, setSidebarPreferredExpanded] =
    useState(true);
  // Temporaril override the user preference due to side effects of user interaction
  const [sidebarInteractionOverride, setSidebarInteractionOverride] =
    useState(false);
  // Top Bar state which contains the wrapped sidebar and contextual sidebar
  const [open, setOpen] = useState(false);

  // Derived / computed state of the sidebar
  const isExpanded = !md
    ? true
    : !xl
      ? sidebarInteractionOverride
      : sidebarPreferredExpanded || sidebarInteractionOverride;

  // Clear interaction when breakpoints change
  useEffect(() => {
    setSidebarInteractionOverride(false);
  }, [md, xl]);

  useEffect(() => {
    // Close Top Bar when navigating away
    if (open) setOpen(false);

    // Clear interaction when navigating away
    setSidebarInteractionOverride(false);
    // oxlint-disable-next-line exhaustive-deps
  }, [pathname, hash]);

  // Handle escape keydown and lock body overflow when topbar is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    if (open) {
      document.body.style.overflow = 'hidden';

      window.addEventListener('keydown', handleEscapeKeyDown);
    }

    return () => {
      document.body.style.overflow = prevOverflow;

      window.removeEventListener('keydown', handleEscapeKeyDown);
    };
  }, [open]);

  const handleClickOutside = (event: MouseEvent) => {
    if (!open) return;

    const target = event.target as Node | null;
    if (!target) return;

    const sidebarElement = sidebarWrapperRef.current;
    if (sidebarElement && sidebarElement.contains(target)) return;

    setOpen(false);
  };

  const toggleSidebarPreference = (
    <SidebarToggle
      onToggle={() => {
        if (xl) {
          // On large viewport, sync user pref immediately and clear interaction
          setSidebarPreferredExpanded(!isExpanded);
          setSidebarInteractionOverride(false);
        } else {
          setSidebarInteractionOverride((v) => !v);
        }
      }}
    />
  );

  const renderedSidebar = sidebar({
    isExpanded,
    toggleSidebarPreference,
    expandSidebarForInteraction() {
      setSidebarInteractionOverride(true);
    },
  });

  const mainRef = useRef<HTMLDivElement | null>(null);
  const sidebarWrapperRef = useRef<HTMLDivElement | null>(null);

  const topBarHeightRef = useHeaderHeightMeasure();
  const topBarHeight = useHeaderHeight();

  return (
    <AngularModalDimmer clipAngular>
      <div
        style={
          {
            '--top-bar-height': `${topBarHeight.height}px`,
          } as CSSProperties
        }
        className={`
          flex bg-background-sidebar-default pt-(--top-bar-height)
          md:flex-row
        `}
      >
        <FocusTrapRegion
          enabled={open}
          render={
            <div
              className={cn(
                'z-1', // always place app shell content (including flyouts) on top of <main/>
                `
                  not-md:fixed not-md:top-0 not-md:w-screen
                  not-md:bg-background-sidebar-default/0
                  not-md:shadow-elevation-sm
                  not-md:transition-[background-color] not-md:duration-200
                  not-md:ease-in-out
                  not-md:motion-reduce:transition-none
                `,
                open && `not-md:z-2 not-md:bg-background-sidebar-default/50`
              )}
              onClick={handleClickOutside}
            >
              <div
                ref={topBarHeightRef}
                id={stickyHeaderId}
                className="md:fixed md:top-0 md:z-1 md:w-screen"
              >
                <TopBar open={open} setOpen={setOpen} />
              </div>

              <SidebarWrapper
                open={open}
                sidebar={renderedSidebar}
                contextualSidebar={contextualSidebar}
                contextualSidebarTargetRef={contextualSidebarTargetRef}
                sidebarWrapperRef={sidebarWrapperRef}
              />
            </div>
          }
        />

        <main
          ref={mainRef}
          className={`
            z-0 flex min-h-screen flex-1 flex-col bg-background-default
            print:min-h-auto
          `}
          data-testid="page-content"
        >
          <div
            className={`
              h-full flex-1 bg-background-default py-2xl
              md:px-3xl
              print:h-auto
              accepted-exception:px-[calc(var(--spacing-xl)+var(--spacing-lg))]
            `}
          >
            {children}

            <img
              src={logoSrc}
              alt="PRISMA Logo"
              aria-hidden={true}
              className={`
                absolute top-[5px] right-[5px] z-10 hidden h-2xl w-[80px]
                print:block
              `}
            />
          </div>

          <ScrollToTopButton mainRef={mainRef} />
          <Footer />
        </main>
      </div>
    </AngularModalDimmer>
  );
};

const SidebarWrapper: FC<{
  sidebar: ReactNode;
  contextualSidebar: ReactNode;
  open: boolean;
  contextualSidebarTargetRef: HTMLDivElement | null;
  sidebarWrapperRef: RefObject<HTMLDivElement | null>;
}> = ({
  sidebar,
  contextualSidebar,
  open,
  // setOpen,
  contextualSidebarTargetRef,
  sidebarWrapperRef,
}) => {
  const { md } = useBreakpoints();
  const reducedMotion = useReducedMotion();

  const [activeSidebar, setActiveSidebar] = useState<
    'app' | 'contextual' | null
  >(null);

  useEffect(() => {
    if (contextualSidebarTargetRef?.hasChildNodes()) {
      setActiveSidebar('contextual');
    } else {
      setActiveSidebar('app');
    }
  }, [open, contextualSidebarTargetRef]);

  // We listen for the focus event here instead of the parent, bc it renders in a portal so we can't catch the focus event there
  useEffect(() => {
    const contextualSidebar = contextualSidebarTargetRef;

    if (!contextualSidebar) return;

    const handleFocusIn = () => {
      setActiveSidebar('contextual');
    };

    contextualSidebar.addEventListener('focusin', handleFocusIn);

    return () => {
      contextualSidebar.removeEventListener('focusin', handleFocusIn);
    };
  }, [contextualSidebarTargetRef]);

  if (md)
    return (
      <div
        ref={sidebarWrapperRef}
        className={`
          sticky top-(--top-bar-height) flex
          print:hidden
        `}
      >
        {sidebar}
        {contextualSidebar}
      </div>
    );

  if (!open) return null;

  return (
    <motion.div
      ref={sidebarWrapperRef}
      className={cn(
        `flex max-w-max flex-nowrap shadow-[8px_0px_16px_rgba(0,0,0,0.2)]`,
        `bg-background-sidebar-default`
      )}
      initial={reducedMotion ? false : { x: '-100px', opacity: 0 }}
      animate={reducedMotion ? false : { x: 0, opacity: 1 }}
      transition={{
        duration: 0.15,
        ease: 'easeInOut',
        delay: 0.1,
      }}
    >
      <div
        onClick={(e) => {
          if (activeSidebar !== 'app') {
            setActiveSidebar('app');
          }
        }}
        onFocus={() => setActiveSidebar('app')}
        className={cn(
          `
            z-3 w-auto transition-[width] duration-300 ease-in-out
            will-change-[width]
          `,
          activeSidebar === 'contextual' &&
            `
              max-[560px]:min-w-[100px] max-[560px]:mask-r-from-0%
              max-[560px]:mask-r-to-100%
            `,
          activeSidebar === 'app' &&
            `
              w-full min-w-auto
              max-[560px]:mask-none
            `
        )}
      >
        <div
          className={cn(
            activeSidebar !== 'app' && `max-[560px]:pointer-events-none`
          )}
        >
          {sidebar}
        </div>
      </div>

      <div
        onClick={(e) => {
          if (activeSidebar !== 'contextual') {
            setActiveSidebar('contextual');
          }
        }}
        className={cn(
          `
            mask-no-repeat
            max-[560px]:relative max-[560px]:mask-l-from-20%
            max-[560px]:mask-l-to-130%
            max-[560px]:before:pointer-events-none max-[560px]:before:absolute
            max-[560px]:before:top-0 max-[560px]:before:left-0
            max-[560px]:before:z-3 max-[560px]:before:hidden
            max-[560px]:before:h-full max-[560px]:before:w-[60%]
            max-[560px]:before:bg-linear-to-r
            max-[560px]:before:from-background-sidebar-default
            max-[560px]:before:to-transparent max-[560px]:before:opacity-70
          `,
          activeSidebar === 'contextual' &&
            `
              max-[560px]:mask-none
              max-[560px]:before:hidden
            `
        )}
      >
        <div
          className={cn(
            activeSidebar !== 'contextual' && `max-[560px]:pointer-events-none`
          )}
        >
          {contextualSidebar}
        </div>
      </div>
    </motion.div>
  );
};

const SidebarToggle: FC<{
  onToggle: () => void;
}> = ({ onToggle }) => {
  return (
    <div className="absolute -right-md z-2 rounded-full shadow-elevation-sm">
      <button
        className={`
          size-2xl cursor-pointer rounded-full bg-background-sidebar-default
          p-xs text-foreground-sidebar-subdued
          hover:bg-background-sidebar-hover
        `}
        onClick={onToggle}
        aria-label="Toggle Navigation"
      >
        <MenuIcon size="sm" />
      </button>
    </div>
  );
};
